// v0.5.0c+7 — `<OffendersTable>` extracted from
// `ActionRequiredBanner.jsx` so the banner stays under the 500-
// LOC cap when the Q1 "still blocked even after flagging"
// pointer landed. Same component contract as before:
//   <OffendersTable
//     failure={...}  // carries .name, .offenders[]
//     calcId={uuid}
//     country={"DE"}
//     periodStart={"YYYY-MM-DD"} periodEnd={"YYYY-MM-DD"}
//   />
// The component fetches the calc's existing drift flags and
// derives `allFlagged` — when every offender on the failure has
// already been drift-flagged, an amber pointer renders below the
// table explaining the only remaining unblock paths.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { listRateTableDriftFlags } from "../../lib/api";
import {
  countrySupportsManualOverride, manualOverrideBoxFor,
} from "../../lib/manualOverrideConfig";
import { OffenderActions } from "./OffenderActions";

const fmtPct = (rate) => {
  const n = Number(rate);
  if (!isFinite(n)) return rate;
  return `${(n * 100).toFixed(n * 100 % 1 === 0 ? 0 : 2)}%`;
};

const fmtMoney = (s) => {
  const n = Number(s);
  if (!isFinite(n)) return s;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

export const OffendersTable = ({
  failure, calcId, country, periodStart, periodEnd,
}) => {
  const { t } = useTranslation();
  const offs = failure?.offenders || [];
  const [flaggedRates, setFlaggedRates] = useState(null);
  useEffect(() => {
    if (!calcId || !country || offs.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await listRateTableDriftFlags(calcId);
        if (cancelled) return;
        // The endpoint returns `{count, flags: [...]}`; tolerate
        // either shape in case a future refactor flattens it.
        const flags = Array.isArray(list) ? list : (list?.flags || []);
        const matches = new Set(
          flags
            .filter((f) => (
              f.country === country
              && (!periodStart || f.period_start === periodStart)
              && (!periodEnd || f.period_end === periodEnd)
            ))
            .map((f) => String(f.rate)),
        );
        setFlaggedRates(matches);
      } catch {
        // Non-fatal — pointer simply won't render. Per-offender
        // row's own state still drives its UI.
        setFlaggedRates(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [calcId, country, periodStart, periodEnd, offs.length]);

  if (offs.length === 0) return null;

  const allFlagged = flaggedRates !== null
    && offs.length > 0
    && offs.every((o) => flaggedRates.has(String(o.rate)));

  return (
    <div
      data-testid={`offenders-${failure.name}`}
      className="mt-2 rounded-md border border-rose-300 dark:border-rose-700/60 bg-white/60 dark:bg-slate-900/60 overflow-hidden"
    >
      <div className="px-3 py-1.5 text-[11px] font-semibold text-rose-700 dark:text-rose-300 border-b border-rose-200 dark:border-rose-800 bg-rose-100/40 dark:bg-rose-950/40">
        {t("ui.country_return.offenders.flagged_rows_title", { defaultValue: "Flagged rows — at these rates:" })}
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-rose-700 dark:text-rose-300 text-left">
            <th className="px-3 py-1 font-medium">Rate</th>
            <th className="px-3 py-1 font-medium text-right">Net</th>
            <th className="px-3 py-1 font-medium text-right">VAT</th>
            <th className="px-3 py-1 font-medium text-right">Transactions</th>
            <th className="px-3 py-1 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {offs.map((o, i) => (
            // v0.6.6 — was key={i}. Each <OffenderActions> owns its own
            // fetched txns/aggregate state and caches it across re-opens
            // (`if (txns.length > 0) return`). An index key means React
            // reuses that SAME instance across a refetch even when the
            // rate at that index changed (offenders can reorder or shrink
            // as rows get resolved) — the drawer would then show the NEW
            // offender's header next to the OLD offender's cached
            // transactions. `rate` is the row's real identity within one
            // failure's offender list (one row per non-recognised rate).
            <tr
              key={String(o.rate)}
              data-testid={`offender-${failure.name}-${i}`}
              className="border-t border-rose-200/70 dark:border-rose-800/70 text-rose-900 dark:text-rose-100"
            >
              <td className="px-3 py-1 tabular-nums">{fmtPct(o.rate)}</td>
              <td className="px-3 py-1 tabular-nums text-right">{fmtMoney(o.net)}</td>
              <td className="px-3 py-1 tabular-nums text-right">{fmtMoney(o.vat)}</td>
              <td className="px-3 py-1 tabular-nums text-right">
                {o.txn_count != null ? o.txn_count : "—"}
              </td>
              <td className="px-3 py-1">
                {calcId && country && (
                  <OffenderActions
                    calcId={calcId} country={country} offender={o}
                    periodStart={periodStart} periodEnd={periodEnd}
                    ruleName={failure.name}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* v0.5.0d Q5 — Banner copy priority. ONE recommended
          action prominent, alternatives secondary. No raw file
          paths. The drift-flagged-but-still-blocked condition
          drives WHICH action is primary:
            - manual-override available (DE/FR): "Route → box X"
            - manual-override not available (ES/UK/IT): "Supersede"
          Drift-flag fallback is secondary in either case
          (recordkeeping). The maintainer-wait is implicit in
          drift-flag and no longer named as a separate path. */}
      {allFlagged && (() => {
        const cc = (country || "").toUpperCase();
        const hasManual = countrySupportsManualOverride(cc);
        const boxId = manualOverrideBoxFor(cc);
        const rowWord = offs.length === 1 ? "row" : "rows";
        return (
          <div
            data-testid={`offenders-still-blocked-${failure.name}`}
            className="px-3 py-2 border-t border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-[11px] leading-snug text-amber-900 dark:text-amber-100"
          >
            <span className="font-bold">{t("ui.country_return.offenders.all_reported_title", { defaultValue: "⚠ Every row reported to maintainer — but this filing is still blocked." })}</span>
            {" "}
            {t("ui.country_return.offenders.all_reported_body", { defaultValue: "Reporting is for the maintainer's queue — a single operator can't change the engine's rate table." })}
            {" "}
            {hasManual ? (
              <>{/* i18n-ignore: pinned by test_v0501c7 */}
                To file today, open one of the flagged rows above and choose
                <span className="font-bold"> Assign to {cc} Box {boxId}</span> (manual-other-rates).
                {/* i18n-ignore: pinned by test_v0501c7 */}{" "}Or
                <span className="font-bold"> Replace this calculation</span>{/* i18n-ignore: pinned by test_v0501c7 */}
                {" "}if the {rowWord} are bad source data, not real sales.
              </>
            ) : (
              <>{/* i18n-ignore: pinned by test_v0501c7 */}
                To file today, open one of the flagged rows above and choose
                <span className="font-bold"> Replace this calculation</span>.{/* i18n-ignore: pinned by test_v0501c7 */}
                {" "}{cc} has no manual-other-rates box on this form — fixing the source data is the only way to file.
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default OffendersTable;
